import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { LanguageService, SupportedLanguage } from './language.service';

interface TranslateResponse {
    translatedText: string;
}

interface MemoryCacheEntry {
    translated: string;
    expiresAt: number;
}

/** Glossário local para termos curtos/médicos que a API costuma devolver iguais ou mal traduzidos. */
const LOCAL_TRANSLATION_GLOSSARY: Partial<Record<SupportedLanguage, Record<string, string>>> = {
    en: {
        Telefone: 'Phone',
        telefone: 'Phone',
        'Cadeira de rodas': 'Wheelchair',
        'Maca coquille': 'Scoop stretcher',
        'Monitorização básica': 'Basic monitoring',
        'Número de telemóvel': 'Mobile phone number',
        Moeda: 'Currency',
    },
    es: {
        Telefone: 'Teléfono',
        telefone: 'Teléfono',
        'Cadeira de rodas': 'Silla de ruedas',
        'Maca coquille': 'Camilla de cuchara',
        'Monitorização básica': 'Monitorización básica',
        'Número de telemóvel': 'Número de móvil',
        Moeda: 'Moneda',
    },
    fr: {
        Telefone: 'Téléphone',
        telefone: 'Téléphone',
        'Cadeira de rodas': 'Fauteuil roulant',
        'Maca coquille': 'Brancard coquille',
        'Monitorização básica': 'Surveillance de base',
        'Número de telemóvel': 'Numéro de mobile',
        Moeda: 'Devise',
    },
    de: {
        Telefone: 'Telefon',
        telefone: 'Telefon',
        'Cadeira de rodas': 'Rollstuhl',
        'Maca coquille': 'Schaufeltrage',
        'Monitorização básica': 'Basisüberwachung',
        'Número de telemóvel': 'Handynummer',
        Moeda: 'Währung',
    },
};

@Injectable({
    providedIn: 'root',
})
export class AutoTranslateService {
    private readonly CACHE_PREFIX = 'autoTranslate_';
    private readonly CACHE_VERSION = '1';
    private readonly MEMORY_CACHE_MAX_ENTRIES = Math.max(100, Number((environment as any).translationMemoryCacheMaxEntries || 3000));
    private readonly MEMORY_CACHE_TTL_MS = Math.max(60_000, Number((environment as any).translationMemoryCacheTtlMs || 6 * 60 * 60 * 1000));
    // Opt-in: por padrão, não persiste em localStorage para evitar custo síncrono e travamentos na UI.
    private readonly enablePersistentCache = (environment as any).translationPersistentCache === true;
    private activeTranslationsCount = 0;
    private isLoading$ = new BehaviorSubject<boolean>(false);
    private pendingRequests: Map<string, Observable<string>> = new Map();
    private memoryCache: Map<string, MemoryCacheEntry> = new Map();
    // Hotfix operacional: termos que não precisam de chamada remota em pt.
    private readonly ptNoopTerms = new Set<string>(['expandir']);

    constructor(private http: HttpClient, private languageService: LanguageService) {
        // Limpar requisições pendentes quando o idioma mudar
        this.languageService.getLanguage$().subscribe(() => {
            this.clearPendingRequests();
        });
    }

    /**
     * Observable que indica se há traduções em andamento
     */
    getIsTranslating$(): Observable<boolean> {
        return this.isLoading$.asObservable();
    }

    /**
     * Verifica se está traduzindo no momento
     */
    isTranslating(): boolean {
        return this.isLoading$.value;
    }

    /**
     * Atualiza o estado de loading
     */
    private updateLoadingState(): void {
        this.isLoading$.next(this.activeTranslationsCount > 0);
    }

    /**
     * Traduz um texto de forma síncrona (para uso em alert/confirm)
     * Retorna uma Promise com o texto traduzido
     */
    async translateSync(text: string, targetLanguage?: SupportedLanguage): Promise<string> {
        try {
            return await firstValueFrom(this.translate(text, targetLanguage));
        } catch (error) {
            console.warn('Erro ao traduzir texto síncrono:', error);
            return text;
        }
    }

    /**
     * Traduz um texto para o idioma atual
     */
    translate(text: string, targetLanguage?: SupportedLanguage): Observable<string> {
        if (!text || !text.trim()) {
            return of(text);
        }

        const trimmedText = text.trim();
        const targetLang = targetLanguage || this.languageService.getCurrentLanguage();

        const localResolved = this.resolveLocalTranslation(trimmedText, targetLang);
        if (localResolved !== null) {
            return of(localResolved);
        }

        // Se o idioma destino é pt-br, não precisa traduzir
        if (targetLang === 'pt-br') {
            return of(trimmedText);
        }

        // Verificar cache
        const cached = this.getFromCache(trimmedText, targetLang);
        if (cached) {
            return of(cached);
        }

        // Verificar se já existe requisição pendente para este texto+idioma
        const requestKey = this.getRequestKey(trimmedText, targetLang);
        const pendingRequest = this.pendingRequests.get(requestKey);
        if (pendingRequest) {
            return pendingRequest;
        }

        // Fazer requisição à API
        this.activeTranslationsCount++;
        this.updateLoadingState();

        // Criar Observable compartilhado com shareReplay para que múltiplos subscribers
        // compartilhem o mesmo resultado
        const translationRequest = this.translateFromApi(trimmedText, targetLang).pipe(
            tap((translated) => {
                // Salvar no cache
                this.saveToCache(trimmedText, targetLang, translated);
            }),
            catchError((error: HttpErrorResponse) => {
                console.warn('Erro ao traduzir texto:', error);
                // Em caso de erro, retornar texto original
                return of(trimmedText);
            }),
            shareReplay(1), // Compartilhar resultado entre múltiplos subscribers
            finalize(() => {
                // Remover do Map de pendentes quando completar
                this.pendingRequests.delete(requestKey);
                this.activeTranslationsCount = Math.max(0, this.activeTranslationsCount - 1);
                this.updateLoadingState();
            }),
        );

        // Adicionar ao Map de requisições pendentes
        this.pendingRequests.set(requestKey, translationRequest);

        return translationRequest;
    }

    /**
     * Traduz múltiplos textos de uma vez (para otimização)
     * Nota: Esta função pode ser usada para otimizar traduções em lote,
     * mas a API LibreTranslate pode ter limites de requisições simultâneas
     */
    translateBatch(texts: string[], targetLanguage?: SupportedLanguage): Observable<string[]> {
        const targetLang = targetLanguage || this.languageService.getCurrentLanguage();

        if (targetLang === 'pt-br') {
            return of(texts);
        }

        // Separar textos com e sem cache
        const textsToTranslate: { text: string; index: number }[] = [];
        const results: string[] = new Array(texts.length);

        texts.forEach((text, index) => {
            if (!text || !text.trim()) {
                results[index] = text;
                return;
            }

            const trimmedText = text.trim();
            const cached = this.getFromCache(trimmedText, targetLang);
            if (cached) {
                results[index] = cached;
            } else {
                textsToTranslate.push({ text: trimmedText, index });
            }
        });

        // Se todos estão em cache, retornar
        if (textsToTranslate.length === 0) {
            return of(results);
        }

        // Traduzir os que não estão em cache sequencialmente para evitar sobrecarga da API
        const translateSequentially = (index: number): Observable<string[]> => {
            if (index >= textsToTranslate.length) {
                return of(results);
            }

            const item = textsToTranslate[index];
            if (index === 0) {
                this.activeTranslationsCount++;
                this.updateLoadingState();
            }
            return this.translateFromApi(item.text, targetLang).pipe(
                tap((translated) => {
                    this.saveToCache(item.text, targetLang, translated);
                    results[item.index] = translated;
                }),
                catchError(() => {
                    results[item.index] = item.text; // Fallback para texto original
                    return of(item.text);
                }),
                // Continuar com o próximo
                switchMap(() => translateSequentially(index + 1)),
                finalize(() => {
                    if (index === textsToTranslate.length - 1) {
                        this.activeTranslationsCount = Math.max(0, this.activeTranslationsCount - 1);
                        this.updateLoadingState();
                    }
                }),
            );
        };

        return translateSequentially(0);
    }

    /**
     * Limpa o cache de traduções
     */
    clearCache(): void {
        this.memoryCache.clear();

        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        try {
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.CACHE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach((key) => localStorage.removeItem(key));
        } catch (error) {
            console.warn('Erro ao limpar cache:', error);
        }
    }

    /**
     * Faz a requisição à API de tradução
     * Primeiro tenta chamar o backend, se falhar, tenta cache local, depois LibreTranslate direto
     */
    private translateFromApi(text: string, targetLanguage: SupportedLanguage): Observable<string> {
        const backendUrl = environment.apiUrl;
        const targetLang = this.languageService.getApiLanguageCode(targetLanguage);

        const payload = {
            text: text,
            targetLanguage: targetLang,
        };

        const tryLibreDirect = (): Observable<string> => {
            // Por padrão, não usar fallback direto no cliente para evitar tempestade de requisições
            // em incidentes do backend de tradução (opt-in via environment.allowDirectTranslateFallback).
            const allowDirectFallback = (environment as any).allowDirectTranslateFallback === true;
            if (!allowDirectFallback) {
                return of(text);
            }

            const libreTranslateUrl = environment.libreTranslateApiUrl || 'https://translate.prehox.com';
            // LibreTranslate espera "pt", não "pt-br"
            const sourceLang = 'pt';

            const libreTranslatePayload = {
                q: text,
                source: sourceLang,
                target: targetLang === 'pt-br' ? 'pt' : targetLang,
                format: 'text',
            };

            return this.http.post<TranslateResponse>(`${libreTranslateUrl}/translate`, libreTranslatePayload).pipe(
                map((response) => {
                    const translated = response.translatedText || text;
                    this.saveToCache(text, targetLanguage, translated);
                    return translated;
                }),
                catchError((error: HttpErrorResponse) => {
                    console.error('Erro na API de tradução (LibreTranslate):', error);
                    return throwError(() => error);
                }),
            );
        };

        // Tentar backend primeiro
        return this.http.post<TranslateResponse>(`${backendUrl}/api/translate`, payload).pipe(
            switchMap((response) => {
                const translated = response.translatedText || text;
                // Backend às vezes devolve 2xx com o texto original (TCP off / fallback silencioso).
                // Tratar como falha para acionar LibreTranslate direto no dev.
                if (translated === text && targetLanguage !== 'pt-br' && targetLanguage !== 'pt') {
                    console.warn('Backend devolveu texto inalterado; tentando LibreTranslate direto.');
                    const cached = this.getFromCache(text, targetLanguage);
                    if (cached) {
                        return of(cached);
                    }
                    return tryLibreDirect();
                }
                return of(translated);
            }),
            catchError((backendError: HttpErrorResponse) => {
                console.warn('Erro ao traduzir via backend, tentando cache local:', backendError);

                const cached = this.getFromCache(text, targetLanguage);
                if (cached) {
                    return of(cached);
                }

                return tryLibreDirect();
            }),
        );
    }

    /**
     * Obtém tradução do cache
     */
    private getFromCache(text: string, targetLanguage: SupportedLanguage): string | null {
        const cacheKey = this.getCacheKey(text, targetLanguage);
        const inMemory = this.getFromMemoryCache(cacheKey);
        if (inMemory) {
            return inMemory;
        }

        if (!this.enablePersistentCache) {
            return null;
        }

        if (typeof window === 'undefined' || !window.localStorage) {
            return null;
        }

        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                this.setMemoryCache(cacheKey, cached);
                return cached;
            }
        } catch (error) {
            console.warn('Erro ao ler cache:', error);
        }

        return null;
    }

    /**
     * Salva tradução no cache
     */
    private saveToCache(text: string, targetLanguage: SupportedLanguage, translated: string): void {
        const cacheKey = this.getCacheKey(text, targetLanguage);
        this.setMemoryCache(cacheKey, translated);

        if (!this.enablePersistentCache) {
            return;
        }

        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        try {
            localStorage.setItem(cacheKey, translated);
        } catch (error) {
            console.warn('Erro ao salvar no cache:', error);
            // Se o LocalStorage estiver cheio, tentar limpar cache antigo
            if (error instanceof DOMException && error.code === 22) {
                this.clearCache();
            }
        }
    }

    /**
     * Gera chave do cache
     */
    private getCacheKey(text: string, targetLanguage: SupportedLanguage): string {
        // Normalizar texto para evitar duplicatas (remover espaços extras, etc)
        const normalizedText = text.replace(/\s+/g, ' ').trim();
        return `${this.CACHE_PREFIX}${this.CACHE_VERSION}_${targetLanguage}_${normalizedText}`;
    }

    /**
     * Gera chave única para requisições pendentes
     */
    private getRequestKey(text: string, targetLanguage: SupportedLanguage): string {
        // Normalizar texto para evitar duplicatas (remover espaços extras, etc)
        const normalizedText = text.replace(/\s+/g, ' ').trim();
        return `${normalizedText}_${targetLanguage}`;
    }

    /**
     * Limpa o Map de requisições pendentes
     */
    private clearPendingRequests(): void {
        this.pendingRequests.clear();
    }

    private resolveLocalTranslation(text: string, targetLanguage: SupportedLanguage): string | null {
        if (targetLanguage === 'pt' && this.ptNoopTerms.has(text.toLowerCase())) {
            return text;
        }

        const glossary = LOCAL_TRANSLATION_GLOSSARY[targetLanguage];
        if (!glossary) {
            return null;
        }
        const hit = glossary[text] ?? glossary[text.toLowerCase()];
        return hit ?? null;
    }

    private getFromMemoryCache(cacheKey: string): string | null {
        const entry = this.memoryCache.get(cacheKey);
        if (!entry) {
            return null;
        }

        if (entry.expiresAt <= Date.now()) {
            this.memoryCache.delete(cacheKey);
            return null;
        }

        // Atualiza ordem (LRU)
        this.memoryCache.delete(cacheKey);
        this.memoryCache.set(cacheKey, entry);
        return entry.translated;
    }

    private setMemoryCache(cacheKey: string, translated: string): void {
        const entry: MemoryCacheEntry = {
            translated,
            expiresAt: Date.now() + this.MEMORY_CACHE_TTL_MS,
        };

        this.memoryCache.delete(cacheKey);
        this.memoryCache.set(cacheKey, entry);

        while (this.memoryCache.size > this.MEMORY_CACHE_MAX_ENTRIES) {
            const oldestKey = this.memoryCache.keys().next().value;
            if (!oldestKey) {
                break;
            }
            this.memoryCache.delete(oldestKey);
        }
    }
}
